import React from 'react';
import {Stack, Divider, styled} from '@mui/material';
import {
  Body,
  Button,
  Icon,
  designSystemToken,
} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {VIEW_TYPES} from '@/types/viewType';
import ColumnSelector, {
  ColumnDefinition,
} from '@/components/column-selector/ColumnSelector';
import DropdownButton, {
  Option,
} from '@/components/dropdown-button/DropdownButton';
import SearchInput from '@/components/search-input';
import TypeSelector from '@/components/type-selector';
import {VIEW_TYPE} from '../../types/creatorTabTypes';
import type {CreatorsViewMode} from '../../types/creatorTabTypes';
import styles from './CreatorsToolbar.module.scss';

export type ViewMode = 'list' | 'grid';

const SHARE_OPTION_ID = {
  CSV: 'csv',
  CreatorsLink: 'creators-link',
  ContentLink: 'content-link',
} as const;

type ShareOptionId = (typeof SHARE_OPTION_ID)[keyof typeof SHARE_OPTION_ID];

function getShareOptions(contentLinkCopied: boolean): Option<ShareOptionId>[] {
  return [
    {id: SHARE_OPTION_ID.CSV, label: translate('CSV')},
    {id: SHARE_OPTION_ID.CreatorsLink, label: translate('Creators Link')},
    {
      id: SHARE_OPTION_ID.ContentLink,
      label: contentLinkCopied
        ? translate('Copied!')
        : translate('Content Link'),
    },
  ];
}

const CreatorsToolbarContainer = styled('div')({
  display: 'grid',
  // Left cell expands to fit search + the inline Shortlist filter button.
  gridTemplateColumns: 'minmax(112px, 560px) 1fr',
  alignItems: 'center',
  columnGap: 16,
});

const ToolbarLeftSection = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  minWidth: 0,
});

const ToolbarRightSection = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 16,
  minWidth: 'max-content',
});

const ToolbarSeparator = styled(Divider)({
  height: 20,
  alignSelf: 'center',
  borderColor: designSystemToken('semantic.bg.neutral-inverse'),
});

interface CreatorsToolbarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showingCount: number;
  onShareCSV?: () => void;
  onShareCreatorsLink?: () => void;
  onShareContentLink?: () => void;
  contentLinkCopied?: boolean;
  onInviteClick: () => void;
  columns: ColumnDefinition[];
  visibleColumns: Record<string, boolean>;
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  activeCreatorsViewMode: CreatorsViewMode;
  onCreatorsViewModeChange: (viewType: CreatorsViewMode) => void;
  // Shortlist filter (optional so the toolbar still works in non-Applications
  // contexts where shortlist isn't supported).
  showShortlistFilter?: boolean;
  isShortlistFilterActive?: boolean;
  onToggleShortlistFilter?: () => void;
  /**
   * Total shortlisted creators in the campaign. When > 0, rendered inline in
   * the filter chip label as "Shortlist · {count}" so the user can see the
   * size of their shortlist at a glance — even when the filter is off.
   */
  shortlistCount?: number;
  showUnreadFilter?: boolean;
  isUnreadFilterActive?: boolean;
  onToggleUnreadFilter?: () => void;
  // Grid-only multi-select toggle. Mirrors the CRM grid pattern: the Table
  // view exposes checkboxes by default via its first column, so the toggle
  // is hidden there. Optional — omit to hide the button entirely.
  isSelectMode?: boolean;
  onToggleSelectMode?: () => void;
  showViewToggle?: boolean;
  showSearch?: boolean;
  showShare?: boolean;
}

function CreatorsToolbar({
  searchQuery,
  onSearchChange,
  showingCount,
  onShareCSV,
  onShareCreatorsLink,
  onShareContentLink,
  contentLinkCopied = false,
  onInviteClick,
  columns,
  visibleColumns,
  onColumnVisibilityChange,
  onCreatorsViewModeChange,
  activeCreatorsViewMode,
  showShortlistFilter = false,
  isShortlistFilterActive = false,
  onToggleShortlistFilter,
  shortlistCount = 0,
  showUnreadFilter = false,
  isUnreadFilterActive = false,
  onToggleUnreadFilter,
  isSelectMode = false,
  onToggleSelectMode,
  showViewToggle = true,
  showSearch = true,
  showShare = true,
}: CreatorsToolbarProps) {
  const shareOptionHandlers: Partial<Record<ShareOptionId, () => void>> = {
    [SHARE_OPTION_ID.CSV]: onShareCSV,
    [SHARE_OPTION_ID.CreatorsLink]: onShareCreatorsLink,
    [SHARE_OPTION_ID.ContentLink]: onShareContentLink,
  };

  const handleShareOptionChange = (option: Option<ShareOptionId>) => {
    shareOptionHandlers[option.id]?.();
  };

  return (
    <CreatorsToolbarContainer className={styles.container}>
      <ToolbarLeftSection>
        {showSearch ? (
          <SearchInput
            value={searchQuery}
            onChange={onSearchChange}
            onSubmit={onSearchChange}
            placeholder={translate('Search applicants...')}
          />
        ) : null}
        {showShortlistFilter || showUnreadFilter ? (
          <Body
            size="sm"
            sx={{
              color: designSystemToken('semantic.fg.inverse-secondary'),
              flexShrink: 0,
            }}
          >
            {translate('Filters')}
          </Body>
        ) : null}
        {showShortlistFilter && onToggleShortlistFilter ? (
          <Button
            size="small"
            mode={isShortlistFilterActive ? 'tinted' : 'plain'}
            appearance="neutral"
            aria-pressed={isShortlistFilterActive}
            onClick={onToggleShortlistFilter}
            className={styles.filterChipButton}
            startIcon={
              <Icon
                size="small"
                appearance="neutral"
                name={
                  isShortlistFilterActive
                    ? 'Navigation-Heart-Fill'
                    : 'Navigation-Heart-Line'
                }
              />
            }
          >
            {shortlistCount > 0
              ? translate('Shortlist · {count}', {count: shortlistCount})
              : translate('Shortlist')}
          </Button>
        ) : null}
        {showUnreadFilter && onToggleUnreadFilter ? (
          <Button
            size="small"
            mode={isUnreadFilterActive ? 'tinted' : 'plain'}
            appearance="neutral"
            aria-pressed={isUnreadFilterActive}
            onClick={onToggleUnreadFilter}
            className={styles.filterChipButton}
            startIcon={
              <Icon
                size="small"
                appearance="neutral"
                name="Navigation-Messages"
              />
            }
          >
            {translate('Unread')}
          </Button>
        ) : null}
      </ToolbarLeftSection>
      <ToolbarRightSection>
        <Body
          size="sm"
          sx={{
            color: designSystemToken('semantic.fg.inverse-secondary'),
            flexShrink: 0,
          }}
        >
          {translate('Showing {count}', {count: showingCount})}
        </Body>
        <ToolbarSeparator orientation="vertical" flexItem />
        {showShare ? (
          <DropdownButton
            label={translate('Share all')}
            buttonClassName={styles.borderlessDropdown}
            size="small"
            mode="plain"
            appearance="neutral"
            options={getShareOptions(contentLinkCopied)}
            onChange={handleShareOptionChange}
            closeAfterSelect={false}
            startIcon={
              <Icon size="small" appearance="neutral" name="Actions-Share" />
            }
            endIcon={<div style={{display: 'none'}} />}
          />
        ) : null}
        <Button
          size="small"
          mode="plain"
          appearance="neutral"
          onClick={onInviteClick}
          startIcon={
            <Icon size="small" appearance="neutral" name="Actions-Apply" />
          }
        >
          {translate('Invite')}
        </Button>
        {activeCreatorsViewMode === VIEW_TYPE.Table ? (
          <ColumnSelector
            columns={columns}
            visibleColumns={visibleColumns}
            canPinColumns={false}
            pinnedColumns={[]}
            onColumnVisibilityChange={onColumnVisibilityChange}
            onColumnPinChange={() => {}}
          />
        ) : null}
        {activeCreatorsViewMode === VIEW_TYPE.Grid && onToggleSelectMode ? (
          <Button
            size="small"
            mode="plain"
            appearance="neutral"
            onClick={onToggleSelectMode}
            startIcon={
              <Icon
                size="small"
                appearance="neutral"
                name={isSelectMode ? 'Features-Check-Filled' : 'Features-Check'}
              />
            }
          >
            {translate(isSelectMode ? 'Selecting' : 'Select')}
          </Button>
        ) : null}
        {showViewToggle ? (
          <TypeSelector
            types={VIEW_TYPES}
            selectedTypeId={activeCreatorsViewMode}
            onChange={(id) => onCreatorsViewModeChange(id as CreatorsViewMode)}
            backgroundColor={designSystemToken(
              'semantic.bg.neutral-inverse-secondary'
            )}
            selectionColor={designSystemToken('reference.gray.300')}
          />
        ) : null}
      </ToolbarRightSection>
    </CreatorsToolbarContainer>
  );
}

export default CreatorsToolbar;
